import { Index, Show, type JSX } from "solid-js";

import clsx from "~/util/html/style";

import { WAIT_PATH_STAGES, WAIT_PATH_VARIANT_STAGES, type WaitStage } from "../art/wait_paths";
import { WaitStageArt } from "../art/wait_stage_art";
import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import { PracticePicture } from "../practice_picture";
import type { IfItWaitsSlide as IfItWaitsSlideData, PictureSrc } from "../types";

// Each step's disc and badge, green now to deep red at the end.
const TONES = ["t0", "t1", "t2", "t3"] as const;

// Read by any path name: a deck can name a path this bundle does not know (a stale tab during a deploy, a frozen copy
// after a rename), and the patient's page draws a plain tooth for it rather than failing.
const STAGES_BY_PATH: Partial<Record<string, readonly WaitStage[]>> = WAIT_PATH_STAGES;
const VARIANTS_BY_PATH: Partial<Record<string, Partial<Record<string, readonly WaitStage[]>>>> =
  WAIT_PATH_VARIANT_STAGES;

// A variant this bundle does not know has captions the path's own drawings would contradict (they end with the
// tooth lost), so it draws the plain tooth too.
function stageFor(slide: IfItWaitsSlideData, step: number): WaitStage {
  const stages = slide.variant
    ? VARIANTS_BY_PATH[slide.path]?.[slide.variant]
    : STAGES_BY_PATH[slide.path];
  return stages?.[step] ?? { art: "tooth", spec: {} };
}

/** Four drawings on a green-to-red rail: what can happen to the tooth, step by step, if treatment waits. A step the
 *  practice has its own picture for shows that picture in the disc instead. A step staff made stand out stays full
 *  while the others fade. */
export function IfItWaitsSlide(props: {
  slide: IfItWaitsSlideData;
  pictureSrc: PictureSrc;
}): JSX.Element {
  const words = useDeckWords();
  return (
    <>
      <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
      <ol class={styles["strip"]}>
        <Index each={props.slide.stages}>
          {(stage, step) => (
            <li
              class={clsx(styles["stg"], styles[TONES[step] ?? "t3"])}
              data-focus={props.slide.focus === step + 1 ? "" : undefined}
              data-faded={
                props.slide.focus !== undefined && props.slide.focus !== step + 1 ? "" : undefined
              }
            >
              {/* Decorative: the drawing is named for the caption printed under it, which a screen reader
                  would otherwise read twice. */}
              <div class={styles["stg-art"]} aria-hidden="true">
                <Show
                  when={stage().practicePicture}
                  fallback={
                    <WaitStageArt stage={stageFor(props.slide, step)} title={stage().caption} />
                  }
                >
                  {(page) => (
                    <PracticePicture
                      page={page()}
                      pictureSrc={props.pictureSrc}
                      alt=""
                      class={styles["stg-photo"]}
                      fallback={
                        <WaitStageArt stage={stageFor(props.slide, step)} title={stage().caption} />
                      }
                    />
                  )}
                </Show>
              </div>
              <div class={styles["stg-n"]}>
                <span>{step + 1}</span>
                <Show when={step === 0}>{words().t("now")}</Show>
              </div>
              <p class={styles["stg-t"]}>{stage().caption}</p>
            </li>
          )}
        </Index>
      </ol>
    </>
  );
}
